import api from './api';

const NotificationService = {
  /**
   * Request permission for notifications
   * @returns {Promise<boolean>} Promise resolving to whether permission was granted
   */
  requestPermission: async () => {
    if (!('Notification' in window)) {
      console.error('This browser does not support desktop notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  },

  /**
   * Check if notifications are enabled
   * @returns {boolean} Whether notifications are enabled
   */
  areNotificationsEnabled: () => {
    return ('Notification' in window) && Notification.permission === 'granted';
  },

  /**
   * Check if push notifications are supported
   * @returns {boolean} Whether push notifications are supported
   */
  isPushSupported: () => {
    return ('serviceWorker' in navigator) && 
           ('PushManager' in window) && 
           ('Notification' in window);
  },

  /**
   * Subscribe to push notifications
   * @returns {Promise<Object>} Promise resolving to the subscription object
   */
  subscribeToPush: async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      throw new Error('Push notifications are not supported in this browser');
    }

    try {
      // Register service worker if not already registered
      let registration;
      try {
        registration = await navigator.serviceWorker.register('/service-worker.js');
        // Wait for the service worker to be ready
        await navigator.serviceWorker.ready;
      } catch (swError) {
        console.error('Service worker registration failed:', swError);
        throw new Error('Failed to register service worker. Push notifications require HTTPS.');
      }

      // Get VAPID public key from server
      const vapidKey = await getApplicationServerKey();
      
      // Get the subscription object
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey
      });

      // Send the subscription to the server
      await api.post('/notifications/subscribe', { subscription: subscription.toJSON() });
      
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      throw error;
    }
  },

  /**
   * Unsubscribe from push notifications
   * @returns {Promise<boolean>} Promise resolving to whether unsubscription was successful
   */
  unsubscribeFromPush: async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        console.log('No service worker registration found');
        return true; // Consider this a success - nothing to unsubscribe from
      }

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        console.log('No push subscription found');
        return true; // Consider this a success - nothing to unsubscribe from
      }

      // Send the unsubscribe request to the server
      try {
        await api.post('/notifications/unsubscribe', { 
          endpoint: subscription.endpoint 
        });
      } catch (apiError) {
        console.warn('Server unsubscribe failed, continuing with local unsubscribe:', apiError);
        // Continue with local unsubscribe even if server fails
      }

      // Unsubscribe locally
      await subscription.unsubscribe();
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  },

  /**
   * Get current push subscription status
   * @returns {Promise<Object|null>} Current subscription or null
   */
  getCurrentSubscription: async () => {
    try {
      if (!('serviceWorker' in navigator)) {
        return null;
      }
      
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return null;
      }
      
      return await registration.pushManager.getSubscription();
    } catch (error) {
      console.error('Error getting current subscription:', error);
      return null;
    }
  },

  /**
   * Get last signal timestamp from local storage
   * @returns {number} Last signal timestamp or 0 if not set
   */
  getLastSignalTimestamp: () => {
    try {
      return parseInt(localStorage.getItem('lastSignalTimestamp') || '0', 10);
    } catch {
      return 0;
    }
  },

  /**
   * Set last signal timestamp in local storage
   * @param {number} timestamp Timestamp to set
   */
  setLastSignalTimestamp: (timestamp) => {
    try {
      localStorage.setItem('lastSignalTimestamp', timestamp.toString());
    } catch (error) {
      console.warn('Failed to save timestamp to localStorage:', error);
    }
  }
};

// Helper function to get the application server key
async function getApplicationServerKey() {
  try {
    const response = await api.get('/notifications/vapid-public-key');
    
    // Axios puts data in response.data
    const publicKey = response.data.publicKey;
    
    if (!publicKey) {
      throw new Error('No VAPID public key received from server');
    }
    
    return urlBase64ToUint8Array(publicKey);
  } catch (error) {
    console.error('Error getting VAPID public key:', error);
    throw new Error('Failed to get push notification configuration from server');
  }
}

// Helper function to convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default NotificationService;